import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { debounce } from "lodash";
import {
  Button,
  Flex,
  SearchField,
  Text,
  TextField,
  View,
} from "@adobe/react-spectrum";
import { toast } from "react-toastify";
import {
  addNotes as addNotesApi,
  getNotes as getNotesApi,
  updateNote as updateNoteApi,
  deleteNote as deleteNoteApi,
} from "../../api/session-api";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { $getRoot, $createParagraphNode } from "lexical";
import "./style.css";
import { ProgressCircle } from "@adobe/react-spectrum";
import Toolbar from "./Toolbar";
import NotesList from "./NotesList";
import PasteHandlerPlugin from "./PasteHandlerPlugin";
import CustomLinkPlugin from "./CustomLinkPlugin";
import { NoteItem, MyNotesProps } from "./types";
import {
  defaultJSON,
  extractPlainTextFromLexical,
  formatRelativeLabel,
} from "./utils";
import MyNotesDefaultPage from "./MyNotesDefaultPage";
import { NoNotesIcon } from "../common/Icons";

// Helper function to sort notes by updated date (newest first)
function sortNotesByDate(notes: NoteItem[]): NoteItem[] {
  return [...notes].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0);
    const dateB = new Date(b.updatedAt || b.createdAt || 0);
    return dateB.getTime() - dateA.getTime();
  });
}

function createEmptyNote(): NoteItem {
  const now = new Date().toISOString();
  return {
    id: `new_${Date.now()}`,
    title: "Untitled",
    updatedLabel: formatRelativeLabel(now),
    excerpt: "",
    content: defaultJSON,
    isNew: true,
    createdAt: now,
    updatedAt: now,
  };
}

const theme = {
  paragraph: "",
  heading: {
    h1: "h1",
    h2: "h2",
    h3: "h3",
  },
  text: {
    bold: "bold",
    italic: "italic",
    underline: "underline",
    strikethrough: "strikethrough",
    underlineStrikethrough: "underline strikethrough",
    boldItalic: "bold italic",
    boldUnderline: "bold underline",
    boldStrikethrough: "bold strikethrough",
    boldUnderlineStrikethrough: "bold underline strikethrough",
    italicUnderline: "italic underline",
    italicStrikethrough: "italic strikethrough",
    italicUnderlineStrikethrough: "italic underline strikethrough",
    boldItalicUnderline: "bold italic underline",
    boldItalicStrikethrough: "bold italic strikethrough",
    boldItalicUnderlineStrikethrough: "bold italic underline strikethrough",
  },
  link: "mynotes-link",
};

const MyNotes = ({ items }: MyNotesProps) => {
  const [notes, setNotes] = useState<NoteItem[]>(
    items && items.length ? items : []
  );
  const [selectedId, setSelectedId] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);

  const selectedNote = useMemo(
    () => notes.find((n) => n.id === selectedId) || null,
    [notes, selectedId]
  );
  const [title, setTitle] = useState<string>(selectedNote?.title || "");
  const [contentJSON, setContentJSON] = useState<string>(
    selectedNote?.content || defaultJSON
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const currentNoteIdRef = useRef<string>("");
  const saveInProgressRef = useRef<boolean>(false);

  useEffect(() => {
    // Update input fields only when selection changes
    if (selectedId) {
      setTitle(selectedNote?.title || "Untitled");
      setContentJSON(selectedNote?.content || defaultJSON);
      setIsEditingTitle(false); // Exit edit mode when selection changes
      setIsSaving(false); // Reset saving state
      setLastSaved(null); // Reset last saved time
      currentNoteIdRef.current = selectedId; // Update ref
    }
  }, [selectedId]);

  // When notes are loaded initially, bind selection's content to editor
  useEffect(() => {
    if (!selectedId && notes.length > 0) {
      setSelectedId(notes[0].id);
      setTitle(notes[0].title || "");
      setContentJSON(notes[0].content || defaultJSON);
    } else if (notes.length === 0) {
      // Clear the editor when no notes exist
      setSelectedId("");
      setTitle("");
      setContentJSON(defaultJSON);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  // Fetch notes on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getNotesApi();
        if (!mounted) return;
        if (Array.isArray(res) && res.length) {
          const loaded: NoteItem[] = res.map((n: any, idx: number) => {
            const content = String(n.content ?? defaultJSON);
            const plain = extractPlainTextFromLexical(content);
            const excerpt = plain ? plain.split("\n")[0].slice(0, 80) : "";
            return {
              id: String(n.id ?? n.noteId ?? idx + 1),
              title: String(n.title ?? "Untitled"),
              updatedLabel: formatRelativeLabel(n.updatedAt ?? n.createdAt),
              excerpt,
              content,
              createdAt: n.createdAt,
              updatedAt: n.updatedAt,
            };
          });

          // Sort notes by updatedAt in descending order (newest first)
          const sortedNotes = sortNotesByDate(loaded);

          setNotes(sortedNotes);
          const first = sortedNotes[0];
          if (first) {
            setSelectedId(first.id);
            setTitle(first.title || "");
            setContentJSON(first.content || defaultJSON);
          }
        } else {
          //const draft = createEmptyNote();
          // setNotes([draft]);
          // setSelectedId(draft.id);
          // setTitle(draft.title);
          // setContentJSON(draft.content);
          console.log("No notes found");
        }
      } catch (e) {
        console.log("Error loading notes", e);
        // const draft = createEmptyNote();
        //setNotes([draft]);
        // setSelectedId(draft.id);
        // setTitle(draft.title);
        // setContentJSON(draft.content);
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Auto-save functionality with debounce
  const autoSaveNote = useCallback(
    async (noteTitle: string, noteContent: string) => {
      if (!selectedId) return;

      const selected = notes.find((n) => n.id === selectedId);
      if (!selected) return;

      // Prevent auto-save if the note is already being saved
      if (isSaving || saveInProgressRef.current) {
        console.log("Auto-save blocked: already saving");
        return;
      }

      // Check if the selectedId has changed since this auto-save was queued
      if (currentNoteIdRef.current && currentNoteIdRef.current !== selectedId) {
        console.log(
          "Auto-save cancelled: selectedId changed from",
          currentNoteIdRef.current,
          "to",
          selectedId
        );
        return;
      }

      // Update refs
      currentNoteIdRef.current = selectedId;
      saveInProgressRef.current = true;

      setIsSaving(true);

      try {
        const isDraft =
          !selected ||
          selected.isNew === true ||
          String(selected.id).startsWith("new_");

        if (isDraft) {
          const res: any = await addNotesApi(noteTitle, noteContent);

          // Handle the correct API response structure
          let createdNote = null;
          let newId = selectedId;
          let newUpdatedAt = new Date().toISOString();

          if (res?.notes && Array.isArray(res.notes) && res.notes.length > 0) {
            // Find the newly created note by matching title and content
            // Look for the most recent note that matches our title and content
            const matchingNote = res.notes.find(
              (note: any) =>
                note.title === noteTitle &&
                (note.content === noteContent ||
                  note.content === "" ||
                  noteContent === "")
            );

            if (matchingNote) {
              createdNote = matchingNote;
              newId = String(createdNote.id);
              newUpdatedAt = String(createdNote.updatedAt);
            } else {
              // Fallback: use the first note (most recent)
              createdNote = res.notes[0];
              newId = String(createdNote.id);
              newUpdatedAt = String(createdNote.updatedAt);
            }
          } else if (res?.id) {
            // Fallback: direct note object
            createdNote = res;
            newId = String(res.id);
            newUpdatedAt = String(res.updatedAt || new Date().toISOString());
          } else {
            console.error("Unexpected API response structure:", res);
            throw new Error(
              "Failed to create note: Invalid response structure"
            );
          }

          const newExcerpt = (() => {
            const plain = extractPlainTextFromLexical(noteContent);
            return plain ? plain.split("\n")[0].slice(0, 80) : "";
          })();

          // Replace the draft note with the created note to prevent duplicates
          setNotes((prev) => {
            // Remove the old draft note
            const filtered = prev.filter((n) => n.id !== selectedId);

            // Check if a note with the new ID already exists (prevent duplicates)
            const existingNote = filtered.find((n) => n.id === newId);
            if (existingNote) {
              return filtered; // Don't add duplicate
            }

            const updatedNote = {
              ...selected,
              id: newId,
              title: noteTitle,
              content: noteContent,
              updatedLabel: formatRelativeLabel(newUpdatedAt),
              excerpt: newExcerpt,
              isNew: false,
              createdAt: selected.createdAt ?? newUpdatedAt,
              updatedAt: newUpdatedAt,
            };

            return [updatedNote, ...filtered];
          });

          // Update the selected ID immediately to prevent future auto-saves from creating duplicates
          setSelectedId(newId);
          setLastSaved(new Date());
        } else {
          await updateNoteApi(selectedId, noteTitle, noteContent);
          setNotes((prev) => {
            const newUpdatedAt = new Date().toISOString();
            const newExcerpt = (() => {
              const plain = extractPlainTextFromLexical(noteContent);
              return plain ? plain.split("\n")[0].slice(0, 80) : "";
            })();

            const updated = prev.map((n) =>
              n.id === selectedId
                ? {
                    ...n,
                    title: noteTitle,
                    content: noteContent,
                    excerpt: newExcerpt,
                    updatedLabel: formatRelativeLabel(newUpdatedAt),
                    updatedAt: newUpdatedAt,
                  }
                : n
            );

            // Sort by updatedAt in descending order (newest first) after update
            return sortNotesByDate(updated);
          });
          setLastSaved(new Date());
        }
      } catch (e) {
        console.log("Error auto-saving note via API", e);
        toast.error("Failed to auto-save note");
      } finally {
        setIsSaving(false);
        saveInProgressRef.current = false;
      }
    },
    [selectedId, notes, isSaving]
  );

  // Debounced auto-save functions
  const debouncedAutoSave = useMemo(
    () => debounce(autoSaveNote, 1000), // 1 second delay
    [autoSaveNote]
  );

  // Handle title changes with auto-save
  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setTitle(newTitle);
      // For auto-save, use trimmed title or "Untitled" if empty/whitespace only
      const trimmedTitle = newTitle.trim();
      const titleForSave = trimmedTitle || "Untitled";
      debouncedAutoSave(titleForSave, contentJSON);
    },
    [debouncedAutoSave, contentJSON]
  );

  // Handle content changes with auto-save
  const handleContentChange = useCallback(
    (newContent: string) => {
      setContentJSON(newContent);
      debouncedAutoSave(title, newContent);
    },
    [debouncedAutoSave, title]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave]);

  const handleDeleteNote = async (id: string) => {
    // Prevent multiple delete calls for the same note
    if (saveInProgressRef.current) {
      return;
    }

    // Set flag to prevent multiple delete operations
    saveInProgressRef.current = true;

    const selected = notes.find((n) => n.id === id);
    const isDraft = selected?.isNew === true || String(id).startsWith("new_");

    try {
      if (!isDraft) {
        await deleteNoteApi(id);
      } else {
      }
      setNotes((prev) => {
        const updated = prev.filter((n) => n.id !== id);
        if (updated.length === 0) {
          // Don't create a new draft note, let the component show MyNotesDefaultPage
          setSelectedId("");
          setTitle("");
          setContentJSON(defaultJSON);
          return [];
        }
        // If we're deleting the currently selected note, select the first available note
        if (selectedId === id) {
          const nextNote = updated[0];
          setSelectedId(nextNote.id);
          setTitle(nextNote.title || "");
          setContentJSON(nextNote.content || defaultJSON);
        }
        return updated;
      });
      toast.success("Note deleted successfully!");
    } catch (e) {
      console.log("Error deleting note via API", e);
      toast.error("Failed to delete note");
    } finally {
      saveInProgressRef.current = false;
    }
  };

  const handleAdd = () => {
    const newNote = createEmptyNote();
    setNotes((prev) => [newNote, ...prev]);
    setSelectedId(newNote.id);
    setTitle(newNote.title);
    setContentJSON(newNote.content);
  };

  const filteredNotes = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    let filtered = notes;

    if (q) {
      filtered = notes.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.content.toLowerCase().includes(q)
      );
    }

    // Always sort by updatedAt time (most recent first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [notes, searchValue]);

  // Auto-select first filtered note when search results change
  useEffect(() => {
    if (filteredNotes?.length > 0 && searchValue?.trim()) {
      // If we have search results and there's a search query, select the first result
      const firstFilteredNote = filteredNotes[0];
      if (firstFilteredNote && firstFilteredNote.id !== selectedId) {
        setSelectedId(firstFilteredNote.id);
        setTitle(firstFilteredNote.title || "Untitled");
        setContentJSON(firstFilteredNote.content || defaultJSON);
      }
    }
  }, [filteredNotes, searchValue, selectedId]);

  // Ensure note selection sets both title and content synchronously
  const handleSelectNote = (id: string) => {
    setSelectedId(id);
    const sel = notes.find((n) => n.id === id);
    setTitle(sel?.title || "Untitled");
    setContentJSON(sel?.content || defaultJSON);
    setIsEditingTitle(false); // Exit edit mode when selecting a note
  };

  const initialConfig = useMemo(
    () => ({
      namespace: "mynotes",
      theme,
      nodes: [
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        LinkNode,
        AutoLinkNode,
      ],
      editorState: (editor: any) => {
        if (contentJSON && contentJSON !== "") {
          try {
            const serial =
              typeof contentJSON === "string"
                ? JSON.parse(contentJSON)
                : contentJSON;
            const parsed = editor.parseEditorState(serial);
            editor.setEditorState(parsed);
            return;
          } catch {
            editor.update(() => {
              const root = $getRoot();
              root.clear();
              const para = $createParagraphNode();
              root.append(para);
            });
            return;
          }
        }
        editor.update(() => {
          const root = $getRoot();
          if (root.getFirstChild() === null) {
            const para = $createParagraphNode();
            root.append(para);
          }
        });
      },
      onError(error: any) {
        console.error(error);
      },
    }),
    [contentJSON]
  );

  if (loading) {
    return (
      <View
        width="100%"
        height="100%"
        backgroundColor="static-white"
        UNSAFE_style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ProgressCircle aria-label="Loading notes" isIndeterminate />
      </View>
    );
  }

  return (
    <View
      width="100%"
      height="100%"
      backgroundColor="static-white"
      UNSAFE_className="mynotes-root"
    >
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        UNSAFE_className="mynotes-header"
      >
        <Text UNSAFE_className="mynotes-header-title">My notes</Text>
        <Flex direction="row" gap="size-200" alignItems="center">
          <SearchField
            width={"260px"}
            value={searchValue}
            onChange={setSearchValue}
            placeholder="Search in My notes"
          />
          <Button variant="cta" onPress={handleAdd}>
            + Add new
          </Button>
        </Flex>
      </Flex>

      <Flex direction="row" UNSAFE_className="mynotes-layout">
        {filteredNotes?.length > 0 && (
          <NotesList
            notes={filteredNotes}
            selectedId={selectedId}
            onSelect={handleSelectNote}
            onDelete={handleDeleteNote}
          />
        )}

        {filteredNotes?.length === 0 ? (
          <Flex
            direction="column"
            flex={1}
            height="100%"
            UNSAFE_className="mynotes-editor"
            alignItems="center"
            justifyContent="center"
          >
            {notes?.length === 0 ? (
              <MyNotesDefaultPage />
            ) : (
              <Flex direction="column" alignItems="center" gap="size-300">
                <Flex
                  justifyContent={"center"}
                  alignItems={"center"}
                  UNSAFE_className="no-chat-icon"
                >
                  <NoNotesIcon />
                </Flex>
                <Flex justifyContent={"center"} UNSAFE_className="no-chat-heading">
                  <Text>No result found</Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        ) : (
          <Flex
            direction="column"
            flex={1}
            height="100%"
            UNSAFE_className="mynotes-editor"
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              UNSAFE_className="mynotes-titlebar"
            >
              <Flex alignItems="center" flex>
                {isEditingTitle ? (
                  <Flex direction="column" flex>
                    <TextField
                      flex
                      label={null as any}
                      aria-label="Title of the note"
                      value={title}
                      onChange={(v) => handleTitleChange(v)}
                      placeholder="Untitled"
                      maxLength={100}
                      width={"500px"}
                      autoFocus
                      onBlur={() => {
                        // Clean up whitespace-only titles when leaving edit mode
                        const trimmedTitle = title.trim();
                        if (!trimmedTitle) {
                          setTitle("Untitled");
                          debouncedAutoSave("Untitled", contentJSON);
                        } else if (trimmedTitle !== title) {
                          setTitle(trimmedTitle);
                          debouncedAutoSave(trimmedTitle, contentJSON);
                        }
                        setIsEditingTitle(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          // Clean up whitespace-only titles when pressing Enter
                          const trimmedTitle = title.trim();
                          if (!trimmedTitle) {
                            setTitle("Untitled");
                            debouncedAutoSave("Untitled", contentJSON);
                          } else if (trimmedTitle !== title) {
                            setTitle(trimmedTitle);
                            debouncedAutoSave(trimmedTitle, contentJSON);
                          }
                          setIsEditingTitle(false);
                        }
                      }}
                    />
                    <Text
                      UNSAFE_style={{
                        fontSize: "11px",
                        color: "#666",
                        marginTop: "2px",
                      }}
                    >
                      {title.trim().length}/100 characters
                      {title.trim() !== title && title.trim().length === 0
                        ? " (whitespace will be removed)"
                        : ""}
                    </Text>
                  </Flex>
                ) : (
                  <div
                    className="mynotes-title-container"
                    onClick={() => setIsEditingTitle(true)}
                  >
                    <Text UNSAFE_className="mynotes-title-text">
                      {title.trim() || "Untitled"}
                    </Text>
                  </div>
                )}
              </Flex>
              <Flex gap="size-200" alignItems="center">
                {isSaving && (
                  <Flex alignItems="center" gap="size-100">
                    <ProgressCircle
                      size="S"
                      aria-label="Saving..."
                      isIndeterminate
                    />
                    <Text UNSAFE_style={{ fontSize: "12px", color: "#666" }}>
                      Saving...
                    </Text>
                  </Flex>
                )}
                {lastSaved && !isSaving && (
                  <Text
                    UNSAFE_style={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Saved {formatRelativeLabel(lastSaved.toISOString())}
                  </Text>
                )}
              </Flex>
            </Flex>

            <LexicalComposer
              key={selectedId}
              initialConfig={initialConfig as any}
            >
              <Toolbar selectedNoteId={selectedId} />

              <View flex UNSAFE_className="mynotes-content-container">
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable className="mynotes-tiptap" />
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                  // placeholder={
                  //   // <div className="editor-placeholder">
                  //   //   Start writing your note...
                  //   // </div>
                  // }
                />
                <HistoryPlugin />
                <ListPlugin />
                <CustomLinkPlugin />
                <PasteHandlerPlugin />
                <OnChangePlugin
                  onChange={(editorState) => {
                    const json = JSON.stringify(editorState.toJSON());
                    handleContentChange(json);
                  }}
                />
              </View>
            </LexicalComposer>
          </Flex>
        )}
      </Flex>
    </View>
  );
};

export default MyNotes;
